const API_BASE_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

export interface DonationCampaign {
  id: string;
  title: string;
  slug: string;
  description: string;
  featuredImage: string;
  targetAmount: number;
  currentAmount: number;
  utilizedAmount: number;
  status: string;
  startDate: string;
  endDate: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  donorCount: number;
  daysLeft?: number;
  donations?: Donation[];
  fundAllocations?: FundAllocation[];
}

export interface Donation {
  id: string;
  campaignId: string;
  userId?: string;
  donorName: string;
  donorEmail: string;
  donorPhone: string;
  isAnonymous: boolean;
  amount: number;
  prayerRequest?: string;
  paymentMethod: string;
  redipayDetails?: any;
  transactionId: string;
  status: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FundAllocation {
  id: string;
  campaignId: string;
  category: string;
  description: string;
  amount: number;
  receipts?: any;
  invoices?: any;
  images?: any;
  notes?: string;
  approvedBy: string;
  approvedAt: string;
  utilizationDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDonationDto {
  campaignId: string;
  donorName: string;
  donorEmail: string;
  donorPhone: string;
  isAnonymous?: boolean;
  amount: number;
  prayerRequest?: string;
}

export interface DonationResponse {
  donation: {
    id: string;
    referenceNo: string;
    amount: number;
    status: string;
  };
  redipayDetails: {
    payment_id: string | null;
    reference_no: string;
    payment_url: string;
    status: string;
    created_date: string;
  };
}

class InfaqService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async getActiveCampaigns(): Promise<DonationCampaign[]> {
    const response = await fetch(`${API_BASE_URL}/infaq/campaigns`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch campaigns');
    }

    return response.json();
  }

  async getCampaignBySlug(slug: string): Promise<DonationCampaign & { daysLeft: number; donorCount: number }> {
    const response = await fetch(`${API_BASE_URL}/infaq/campaigns/${slug}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch campaign');
    }

    return response.json();
  }

  async createDonation(donationData: CreateDonationDto): Promise<DonationResponse> {
    const response = await fetch(`${API_BASE_URL}/infaq/donate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(donationData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create donation');
    }

    return response.json();
  }

  async getDonationReceipt(donationId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/infaq/receipt/${donationId}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch receipt');
    }

    return response.json();
  }

  async getStatistics(): Promise<{ totalRaised: number; totalDonors: number; activeCampaigns: number }> {
    const response = await fetch(`${API_BASE_URL}/infaq/statistics`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch statistics');
    }

    return response.json();
  }

  // Admin methods

  async createCampaign(campaignData: {
    title: string;
    slug: string;
    description: string;
    featuredImage?: string;
    targetAmount: number;
    startDate: string;
    endDate?: string;
  }): Promise<DonationCampaign> {
    const response = await fetch(`${API_BASE_URL}/infaq/campaigns`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(campaignData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create campaign');
    }

    return response.json();
  }

  /** Upload a campaign main image; resolve with the public URL to store in
   *  featuredImage. Server enforces JPEG/PNG/WebP/GIF, max 5 MB. */
  async uploadCampaignImage(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/infaq/campaigns/image`, {
      method: 'POST',
      headers: this.getAuthHeadersForUpload(),
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to upload image');
    }

    return response.json();
  }

  private getAuthHeadersForUpload(): HeadersInit {
    // Same auth header as getAuthHeaders(), but no Content-Type so the
    // browser sets the multipart boundary itself.
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getAllCampaigns(): Promise<DonationCampaign[]> {
    const response = await fetch(`${API_BASE_URL}/infaq/admin/campaigns`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch campaigns');
    }

    return response.json();
  }

  async updateCampaign(id: string, updateData: Partial<DonationCampaign>): Promise<DonationCampaign> {
    const response = await fetch(`${API_BASE_URL}/infaq/admin/campaigns/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error('Failed to update campaign');
    }

    return response.json();
  }

  async deleteCampaign(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/infaq/admin/campaigns/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete campaign');
    }
  }

  async getAllDonations(filters?: {
    campaignId?: string;
    status?: string;
    donorName?: string;
    donorEmail?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    donations: Donation[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const response = await fetch(`${API_BASE_URL}/infaq/admin/donations?${params.toString()}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch donations');
    }

    return response.json();
  }

  async allocateFunds(
    campaignId: string,
    allocationData: {
      category: string;
      description: string;
      amount: number;
      receipts?: string[];
      invoices?: string[];
      images?: string[];
      notes?: string;
    },
  ): Promise<FundAllocation> {
    const response = await fetch(`${API_BASE_URL}/infaq/admin/campaigns/${campaignId}/allocate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(allocationData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to allocate funds');
    }

    return response.json();
  }

  async getDetailedAnalytics(): Promise<{
    overview: {
      totalRaised: number;
      totalUtilized: number;
      totalRemaining: number;
      totalDonations: number;
      activeCampaigns: number;
      completedCampaigns: number;
    };
    recentDonations: Donation[];
    topCampaigns: (DonationCampaign & { donorCount: number })[];
    donationStatsByStatus: {
      status: string;
      count: number;
      totalAmount: number;
    }[];
  }> {
    const response = await fetch(`${API_BASE_URL}/infaq/admin/analytics`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch analytics');
    }

    return response.json();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('ms-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  calculateProgress(current: number, target: number): number {
    return Math.min((current / target) * 100, 100);
  }
}

export const infaqService = new InfaqService();
